
import React, { useEffect, useState, useRef } from "react";
import { GoogleMap, Marker, DirectionsRenderer, useJsApiLoader } from "@react-google-maps/api";
import "./orders.css";

const Orders = () => {
  const [bookings, setBookings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const [user, setUser] = useState(null);
  const pollingRef = useRef(null);

  const statusOptions = [
    { value: "assigned", label: "Assigned" },
    { value: "cancelled", label: "Cancelled" },
  ];

  // Initial fetch and start polling
  useEffect(() => {
    fetchBookings();
    const storedUser = localStorage.getItem("owner_user");
    if (storedUser) setUser(JSON.parse(storedUser));

    pollingRef.current = setInterval(fetchBookings, 10000);
    return () => clearInterval(pollingRef.current);
  }, []);

  // Fetch bookings
  const fetchBookings = async () => {
    try {
      const res = await fetch(
        "https://www.actrofiteinfotech.com/rootservices/dashboard/booking.php"
      );
      const data = await res.json();
      if (data.status === "success") {
        // Only show non-cancelled orders
        const shippingOrders = data.data.filter(
          (b) => b.order_status === "shipping"
        );

        setBookings((prev) => {
          const existingIds = new Set(prev.map((b) => b.id));
          const newOrders = shippingOrders.filter((b) => !existingIds.has(b.id));
          // Sort by booked_at ascending (oldest first, latest at bottom)
          const combined = [...prev, ...newOrders].sort(
            (a, b) => new Date(a.booked_at) - new Date(b.booked_at)
          );
          return combined;
        });
      }
    } catch (error) {
      console.error("Failed to fetch bookings:", error);
    }
  };

  // Fetch employees
  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const res = await fetch(
        "https://www.actrofiteinfotech.com/rootservices/dashboard/booking.php?type=employees"
      );
      const data = await res.json();
      if (data.status === "success") setEmployees(data.data);
      else setEmployees([]);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const openModal = (booking) => {
    const initialStatus = ["assigned", "cancelled"].includes(booking.order_status)
      ? booking.order_status
      : "assigned";

    setSelectedBooking(booking);
    setSelectedStatus(initialStatus);
    setSelectedEmployeeId(null);
    setIsModalOpen(true);
    if (initialStatus === "assigned") fetchEmployees();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBooking(null);
    setSelectedStatus("");
    setSelectedEmployeeId(null);
  };

  const updateBooking = async () => {
    if (!selectedBooking) return;

    if (selectedStatus === "assigned" && !selectedEmployeeId) {
      alert("Please select an employee before updating.");
      return;
    }

    try {
      const res = await fetch(
        "https://www.actrofiteinfotech.com/rootservices/dashboard/booking.php?type=update",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: selectedBooking.id,
            order_status: selectedStatus,
            assigned_employee_id: selectedEmployeeId,
          }),
        }
      );

      const data = await res.json();
      if (data.status === "success") {
        setBookings((prev) =>
          prev
            .map((b) =>
              b.id === selectedBooking.id
                ? {
                    ...b,
                    order_status: selectedStatus,
                    assigned_employee_id: selectedEmployeeId,
                    employee_name:
                      employees.find((e) => e.employee_id === selectedEmployeeId)
                        ?.full_name || null,
                  }
                : b
            )
            .filter((b) => b.order_status !== "cancelled") // remove cancelled
        );
        closeModal();
      } else {
        alert("Update failed: " + data.message);
      }
    } catch (error) {
      alert("Error updating booking.");
      console.error(error);
    }
  };

  return (
    <div className="orders-containerorder">
      {/* User Info */}
      {user && (
        <div className="orders-user-infoorder">
          <h3>Welcome, {user.username}</h3>
          <p>📍 {user.city}, {user.state}</p>
        </div>
      )}

      {/* Heading with count */}
      <h2 className="orders-headingorder">
        📦 Booking Orders  ({bookings.length})
      </h2>

      {bookings.length === 0 && <p>❌ No orders found.</p>}

      {bookings.map((b) => (
        <div key={b.id} className="order-cardorder" onClick={() => openModal(b)}>
          <div className="order-imageorder">
            <img src={b.image_url || b.profile_pic || "/placeholder.jpg"} alt="Service" />
          </div>

          <div className="order-contentorder">
            <div className="order-headorder">
              <strong>{b.name}</strong> | {b.email}
            </div>

            <div className="order-infoorder">
              <p><strong>Service: </strong>{b.main_service} → {b.sub_service_title}</p>
              {b.custom_title && <p><strong>Custom Title: </strong>{b.custom_title}</p>}
              {b.custom_description && <p><strong>Notes: </strong>{b.custom_description}</p>}
              {b.address && <p><strong>Booking Address: </strong>{b.address}</p>}
              {b.latitude && b.longitude && (
                <p>
                  <strong>Coordinates: </strong>
                  {b.latitude}, {b.longitude}{" "}
                  <a href={`https://www.google.com/maps?q=${b.latitude},${b.longitude}`} target="_blank" rel="noopener noreferrer">View Map</a>
                </p>
              )}
              <p>
                <strong>Booked At: </strong>
                {new Date(b.booked_at + " UTC").toLocaleString("en-IN", {
                  timeZone: "Asia/Kolkata",
                  hour12: true,
                  weekday: "short",
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </p>
              <p><strong>Phone: </strong>{b.user_phone}</p>
              {b.alternate_phone && <p><strong>Alternate Phone: </strong>{b.alternate_phone}</p>}
            </div>

            {/* <div className="order-footerorder">
              <p><strong>Price: </strong>₹{b.discount_price || b.original_price}</p>
              <p><strong>Status: </strong>{b.order_status.replace(/_/g, " ")}</p>
              <p><strong>Assigned Employee: </strong>{b.employee_name || "None"}</p>
            </div> */}
            <div className="order-footerorder">
  <p>
    <strong>Price: </strong>
    {b.discount_price ? (
      <>
        <span style={{ textDecoration: "line-through", marginRight: "5px" }}>₹{b.original_price}</span>
        <span>₹{b.discount_price}</span>
      </>
    ) : (
      <span>₹{b.original_price}</span>
    )}
  </p>
  <p>
    <strong>Status: </strong>
    {b.order_status.replace(/_/g, " ")}
  </p>
  <p>
    <strong>Assigned Employee: </strong>
    {b.employee_name || "None"}
  </p>
</div>

          </div>
        </div>
      ))}

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlayorder">
          <div className="modal-contentorder">
            <h3>Update Booking for {selectedBooking?.name}</h3>

            <label>
              Status:
              <select
                value={selectedStatus}
                onChange={(ev) => setSelectedStatus(ev.target.value)}
                className="status-dropdownorder"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>

            {selectedStatus === "assigned" && (
              <>
                <p><strong>Currently Assigned Employee: </strong>{selectedBooking?.employee_name || "None"}</p>
                <label>
                  Assign Employee:
                  {loadingEmployees ? (
                    <p>Loading employees...</p>
                  ) : (
                    <select
                      value={selectedEmployeeId || ""}
                      onChange={(ev) => setSelectedEmployeeId(ev.target.value ? Number(ev.target.value) : null)}
                      className="status-dropdownorder"
                    >
                      <option value="">-- Select Employee --</option>
                      {employees.map((emp) => (
                        <option key={emp.employee_id} value={emp.employee_id}>
                          {emp.full_name} ({emp.designation})
                        </option>
                      ))}
                    </select>
                  )}
                </label>
              </>
            )}

            <div className="modal-buttonsorder">
              <button onClick={updateBooking}>Update</button>
              <button onClick={closeModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;

















